import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { authApi, organizationApi } from '@/lib/pillarApi';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import type { ActiveRoleAssignmentResponse } from '@anchorbase/pillar-sdk';
import { hasInternalAdminPrivileges } from '@anchorbase/pillar-sdk';
import type {
    OrganizationCapacityKey,
    OrganizationOmnisearchListItem,
    OrganizationOmnisearchPageResponse,
} from '@anchorbase/pillar-sdk';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { getOrganizationCapacityFilter } from '@/lib/organization-capacity';

interface OrganizationsListPageProps {
    organizationCapacityKey: OrganizationCapacityKey;
    title: string;
    cardTitle: string;
    createPath?: string;
    createButtonLabel?: string;
    queryKeyPrefix: ReadonlyArray<string>;
}

function buildMerchantAccountTupleText(
    organization: OrganizationOmnisearchListItem
): ReadonlyArray<string> {
    if (organization.merchant_accounts === undefined || organization.merchant_accounts.length === 0) {
        return ['No merchant accounts'];
    }
    return organization.merchant_accounts.map(
        (merchantAccount) =>
            merchantAccount.surcharge_enabled
                ? `MID: ${merchantAccount.processor_mid}, surcharge $${merchantAccount.surcharge_pct}%`
                : `MID: ${merchantAccount.processor_mid}, no surcharge`
    );
}

function buildOrganizationCardTitle(organization: OrganizationOmnisearchListItem): string {
    if (organization.is_merchant !== true) {
        return organization.name;
    }

    const dba: string | undefined = organization.businessDetails?.dba?.trim();
    if (dba !== undefined && dba.length > 0) {
        return dba;
    }

    const legalEntityName: string | undefined =
        organization.businessDetails?.legalEntityName?.trim();
    if (legalEntityName !== undefined && legalEntityName.length > 0) {
        return legalEntityName;
    }

    return organization.name;
}

export function OrganizationsListPage(props: OrganizationsListPageProps) {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const organizationListsVersion: number = useDataInvalidationStore(
        (state) => state.organizationListsVersion
    );
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            ...props.queryKeyPrefix,
            `orgs=${organizationListsVersion}`,
            `showDeleted=${showDeleted}`,
        ],
        [organizationListsVersion, props.queryKeyPrefix, showDeleted]
    );

    const { data: activeRoleAssignment, isLoading: isActiveRoleAssignmentLoading } =
        useQuery<ActiveRoleAssignmentResponse | null>({
            queryKey: ['activeRoleAssignment'],
            queryFn: async () => {
                return await authApi.getActiveRoleAssignment();
            },
            staleTime: 30000,
        });

    const canCreateOrganizations: boolean = hasInternalAdminPrivileges(
        activeRoleAssignment ?? null
    );
    const defaultIncludeDescendants: boolean =
        props.organizationCapacityKey === 'is_merchant'
        && activeRoleAssignment?.organization_uuid !== null
        && activeRoleAssignment?.organization_uuid !== undefined
        && activeRoleAssignment.organization_is_iso === true
        && activeRoleAssignment.organization_is_merchant !== true;
    const [includeDescendantsOverride, setIncludeDescendantsOverride] =
        useState<boolean | null>(null);
    const includeDescendants: boolean =
        includeDescendantsOverride ?? defaultIncludeDescendants;

    if (isActiveRoleAssignmentLoading) {
        return <div>Loading...</div>;
    }

    return (
        <InfiniteScrollList<OrganizationOmnisearchListItem, string, ReadonlyArray<string>>
            pageTitle={props.title}
            cardTitle={props.cardTitle}
            queryPlaceholder="Search..."
            queryKeyPrefix={[...queryKeyPrefix, `descendants=${includeDescendants}`]}
            pageSize={50}
            fetchPage={async (params) => {
                const response: OrganizationOmnisearchPageResponse =
                    await organizationApi.searchOrganizations({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        ...getOrganizationCapacityFilter(props.organizationCapacityKey),
                        include_descendants: includeDescendants,
                        limit: params.limit,
                        cursor: params.cursor,
                        show_deleted: showDeleted ? true : undefined,
                    });
                return { items: response.organizations, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().trim().min(1).safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected opaque cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading organizations...',
                emptyLabel: 'No organizations found.',
                endOfListLabel: 'End of organizations.',
                genericErrorLabel: 'Failed to load organizations.',
            }}
            headerActions={
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm">
                        <Checkbox
                            checked={includeDescendants}
                            onCheckedChange={(checked) => {
                                setIncludeDescendantsOverride(checked === true);
                            }}
                        />
                        <span>Show subsidiaries</span>
                    </label>
                    {canCreateOrganizations &&
                    props.createPath !== undefined &&
                    props.createButtonLabel !== undefined ? (
                        <Button
                            id="create-organization-button"
                            onClick={() => {
                                breadcrumbNavigate({
                                    source: 'panel',
                                    target: {
                                        url: props.createPath!,
                                        title: props.createButtonLabel!,
                                    },
                                });
                            }}
                        >
                            {props.createButtonLabel}
                        </Button>
                    ) : null}
                </div>
            }
            controls={
                canCreateOrganizations ? (
                    <div className="flex items-center gap-2">
                        <Label htmlFor="organization-show-deleted" className="cursor-pointer">
                            Show deleted
                        </Label>
                        <Checkbox
                            id="organization-show-deleted"
                            checked={showDeleted}
                            onCheckedChange={(checked) => {
                                setShowDeleted(checked === true);
                            }}
                        />
                    </div>
                ) : undefined
            }
            renderItems={({ items }) => {
                const visibleOrganizations: ReadonlyArray<OrganizationOmnisearchListItem> =
                    items.filter((org) =>
                        showDeleted ? org.status === 'DELETED' : org.status !== 'DELETED'
                    );
                if (visibleOrganizations.length === 0) {
                    return <p className="text-sm text-gray-600">No organizations found.</p>;
                }
                return (
                    <div className="space-y-2">
                        {visibleOrganizations.map((org: OrganizationOmnisearchListItem) => (
                            <button
                                key={org.organization_uuid}
                                data-organization-uuid={org.organization_uuid}
                                type="button"
                                className="w-full text-left p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                                onClick={() => {
                                    breadcrumbNavigate({
                                        source: 'panel',
                                        target: {
                                            url: `/organizations/${org.organization_uuid}`,
                                            title: 'Organization Details',
                                        },
                                    });
                                }}
                            >
                                <div className="flex justify-between items-start gap-8">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold">
                                                {buildOrganizationCardTitle(org)}
                                            </h3>
                                            <StatusBadge status={org.status} />
                                        </div>
                                        {org.businessDetails !== undefined && (
                                            <div className="mt-1 space-y-0.5">
                                                <p className="text-xs text-muted-foreground">
                                                    {org.businessDetails.legalEntityName}
                                                </p>
                                                {org.businessDetails.dba !== undefined && (
                                                    <p className="text-xs text-muted-foreground">
                                                        DBA: {org.businessDetails.dba}
                                                    </p>
                                                )}
                                                {org.businessDetails.department !== undefined && (
                                                    <p className="text-xs text-muted-foreground">
                                                        Department: {org.businessDetails.department}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <p className="text-xs font-medium text-muted-foreground">
                                            Merchant accounts
                                        </p>
                                        <div className="text-xs text-muted-foreground text-right space-y-0.5">
                                            {buildMerchantAccountTupleText(org).map((tupleText: string) => (
                                                <p key={tupleText}>{tupleText}</p>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                );
            }}
        />
    );
}
